import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';

import { v4 as uuid } from 'uuid';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ElementTypes, GaContext } from '@fcom/common/interfaces/gtm.interface';
import { MenuItem } from '@fcom/common/interfaces/menu.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { SidebarNavTheme } from './enums';

@Component({
  selector: 'fcom-sidebar-nav',
  styleUrls: ['./sidebar-nav.component.scss'],
  templateUrl: './sidebar-nav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidebarNavComponent {
  private themeService = inject(FinnairThemeService);

  title = input<string | undefined>(undefined);
  showTitle = input<boolean | null>(true);
  topSpacing = input<boolean>(true);
  theme = input<SidebarNavTheme>(SidebarNavTheme.EMBLEM_LEFT);
  bottomSpacing = input<boolean>(true);
  dataTestId = input<string>('sidebar-nav');
  tracking = input<boolean>(true);
  trackingLabel = input<string | undefined>('level 1: ');
  /**
   * FAQ like pages had nested version, with the possibility to render text from 'linkText', this input is for that use-case
   */
  nested = input<boolean>(false);
  mobileExpandClasses = input<string>('');

  activeNavItem = input<MenuItem>();
  activeNavSection = input<MenuItem>();

  /**
   * Determines whether clicks on desktop should be emitted.
   * Use-case example is refund status page which has an exit link that should trigger custom handling.
   */
  allowCustomFunction = input<boolean>(false);

  readonly ElementTypes = ElementTypes;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly SidebarNavTheme = SidebarNavTheme;
  readonly TRACKING_CONTEXT = GaContext.SECTION_NAVIGATION;
  readonly triggerId = uuid();
  readonly contentId = uuid();
  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  expanded = model(false);

  /**
   * Emitted when an item link with custom behaviour (e.g. a pseudo-link such as `'#'`) is
   * clicked, so consumers can hook in custom handling (like MMB's "refetch order" item)
   * alongside regular `finLink` navigation.
   */
  itemClicked = output<{ item: MenuItem; event: Event }>();

  toggleExpand(event: MouseEvent): void {
    // The show details link is inside another event handler and triggers twice. Stop this event
    // from propagating.
    event.stopPropagation();
    this.expanded.update((v) => !v);
  }
}
